import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { isStaffRole } from "@/lib/roles";

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#fafaf9]">
    <div className="animate-spin w-8 h-8 border-4 border-[#bd9a59] border-t-transparent rounded-full" />
  </div>
);

// Where each role belongs by default — used for cross-role bounces so users
// always end up in their proper portal instead of getting kicked to login.
const homeForRole = (role) => {
  // HF-Team-Vis-1 (2026-07-29) — junior_broker is staff too (see isStaff
  // in AuthContext.jsx); without this a junior_broker who got bounced by
  // any allowRoles check would land on /my-applications, not /admin.
  if (isStaffRole(role)) return "/admin";
  if (role === "introducer") return "/introducer";
  // Portals-3 (2026-06-21) — partner role has its own portal.
  if (role === "partner") return "/partner/dashboard";
  return "/my-applications"; // role === "user" or anything else
};

// PR #100 / AUM-9 — every authenticated route checks must_change_password and
// bounces to /change-password until the flag clears. Defined here so the gate
// is impossible to forget at the route level. Excluded paths: /change-password
// itself (otherwise we'd infinite-loop) and /logout if it existed.
function useMustChangeRedirect() {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return null;
  if (!user.must_change_password) return null;
  if (location.pathname === "/change-password") return null;
  return <Navigate to="/change-password" replace />;
}

// Generic protected route. By default any authenticated user can pass.
// Pass `allowRoles` to restrict — out-of-role users get bounced to their home.
export const ProtectedRoute = ({ children, allowRoles }) => {
  const { user, loading } = useAuth();
  const mustChangeBounce = useMustChangeRedirect();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (mustChangeBounce) return mustChangeBounce;
  if (allowRoles && !allowRoles.includes(user.role)) {
    return <Navigate to={homeForRole(user.role)} replace />;
  }
  return children;
};

// Admin/broker only. Out-of-role authenticated users go to their portal,
// unauthenticated users go to login.
export const AdminRoute = ({ children }) => {
  const { user, loading, isStaff } = useAuth();
  const mustChangeBounce = useMustChangeRedirect();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (mustChangeBounce) return mustChangeBounce;
  if (!isStaff) return <Navigate to={homeForRole(user.role)} replace />;
  return children;
};

// Introducer only. Out-of-role authenticated users go to their portal,
// unauthenticated users go to login.
export const IntroducerRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const mustChangeBounce = useMustChangeRedirect();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (mustChangeBounce) return mustChangeBounce;
  if (user.role !== "introducer") return <Navigate to={homeForRole(user.role)} replace />;
  return children;
};

// Portals-3 (2026-06-21) — Partner only. Out-of-role authenticated users go to
// their portal, unauthenticated users go to login. Partners are also
// introducers by default (is_also_introducer=True) but they live in the
// Partner Portal (which surfaces their direct intros as a first-class section)
// rather than the introducer one.
export const PartnerRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const mustChangeBounce = useMustChangeRedirect();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (mustChangeBounce) return mustChangeBounce;
  if (user.role !== "partner") return <Navigate to={homeForRole(user.role)} replace />;
  return children;
};
